import { graphql, QueryOpts, ChildProps } from 'react-apollo'
import { RouteComponentProps } from 'react-router-dom'
import { get, pick } from 'lodash'
import getCountry from '../../helpers/get-country'
import { ApplicationError, NotFoundError } from '../../helpers/errors'
import {
  CFPromotion,
  CFComparisonModule,
  CFTermsModule,
} from '../../../types/contentful'
import {
  ContentPageDisplayModes,
  PromotionFeature,
  Promotion,
  ComparisonModule,
  MetaData,
  NavigationGroup,
  Variables,
  Props,
  TermsModule,
  Response,
} from '../../../types/content-page'
import { WITH_CONTENT_PAGE_QUERY } from './query'

export const query = WITH_CONTENT_PAGE_QUERY

export const removeLeadingSlash = (str: string): string =>
  str.replace(/^\/+/g, '')

const createFeaturePromotion = (entry: CFPromotion): PromotionFeature => ({
  sys: { contentTypeId: 'promotion' },
  displayMode: ContentPageDisplayModes.FEATURE,
  title: entry.title,
  body: entry.shortDescription,
  shortDescription: entry.shortDescription,
  desktopImageUrl: entry.image.url,
  mobileImageUrl: entry.image.url,
  callToAction: {
    address: entry.callToActionAddress,
    category: entry.callToActionLabel,
  },
})

const createPromotion = (entry: CFPromotion): Promotion => ({
  sys: { contentTypeId: 'promotion' },
  displayMode: ContentPageDisplayModes.PROMOTION,
  title: entry.title,
  shortDescription: entry.shortDescription,
  heading: entry.heading,
  image: entry.image,
  callToActionAddress: entry.callToActionAddress,
  callToActionLabel: entry.callToActionLabel,
})

const createComparisonModule = (
  entry: CFComparisonModule
): ComparisonModule => ({
  type: entry.__typename,
  displayMode: ContentPageDisplayModes.COMPARISON_ITEM,
  entries: entry.comparisonModuleCollection.items.map(info => ({
    productName: info.productName,
    dotPoints: info.valuePropositions,
    callToAction: {
      url: info.callToAction.url,
      category: info.callToAction.title,
    },
    utilityBox: info.utilityPanel && {
      heading: info.utilityPanel.heading,
      items: info.utilityPanel.contentEntriesCollection.items,
    },
    heroImageSet: info.heroImageSet,
  })),
})

const createContentPageModules = (
  item: CFComparisonModule | CFPromotion | CFTermsModule,
  i: number
): PromotionFeature | Promotion | ComparisonModule | TermsModule | null => {
  switch (item.__typename) {
    case 'CFPromotion':
      return i === 0
        ? createFeaturePromotion(item as CFPromotion)
        : createPromotion(item as CFPromotion)
    case 'CFComparisonModule':
      return createComparisonModule(item as CFComparisonModule)
    case 'CFTerms':
      return item as TermsModule
    default:
      return null
  }
}

export const mapPropsToOptions = ({
  staticContext,
  match,
}: RouteComponentProps): QueryOpts<Variables> => {
  const country = getCountry(staticContext)

  // we'll remove the leading slash from the path, if it exists
  const path = removeLeadingSlash(match.path)

  return {
    errorPolicy: 'ignore',
    context: { version: 2 },
    variables: {
      path,
      country: [country],
    },
  }
}

export const mapDataToProps = ({
  data,
}: Response): ChildProps<Props, Response, Variables> => {
  if (!data) {
    return { error: new ApplicationError('No data') }
  }
  const { cfContentPageCollection, loading, error } = data

  if (error) {
    return {
      error: new ApplicationError(error.message),
    }
  } else if (loading) {
    return { loading }
  } else if (
    !cfContentPageCollection ||
    !cfContentPageCollection.items.length
  ) {
    return {
      error: new NotFoundError(),
    }
  }

  const modules: (
    | PromotionFeature
    | Promotion
    | ComparisonModule
    | TermsModule
  )[] = get(
    cfContentPageCollection,
    ['items', '0', 'modulesCollection', 'items'],
    []
  ).map((item: CFComparisonModule | CFPromotion | CFTermsModule, i: number) =>
    createContentPageModules(item, i)
  )

  const metaData: MetaData = pick(
    get(cfContentPageCollection, ['items', '0'], {}),
    ['shortDescription', 'title', 'path', 'country', 'metaTitle']
  )

  const navigationMenu:
    | (NavigationGroup | NavigationGroup)[]
    | undefined = get(cfContentPageCollection, ['items', '0', 'navigationMenu'])

  return { modules, metaData, navigationMenu }
}

const withContentPage = graphql<any, Response, Variables>(
  WITH_CONTENT_PAGE_QUERY,
  {
    options: mapPropsToOptions,
    props: mapDataToProps,
  }
)

export default withContentPage
